import type { ElementMenuItem } from '../context/elementContextMenuContext';
import { ClipboardCheckIcon, Copy01Icon, Scissors01Icon, Trash02Icon } from '@milanote/roma-icons';
import { ElementContextMenuPredicates } from './elementContextMenuPredicates';

const {
    canClone,
    canDuplicateAliasToBoard,
    canPaste,
    canPasteStyles,
    canRename,
    isApplePlatform,
    isSingleBoard,
    supportsOpenInNewTab,
} = ElementContextMenuPredicates;

const openInNewTab: ElementMenuItem<'openInNewTab'> = {
    type: 'action',
    id: 'openInNewTab',
    localeKey: 'element.openInNewTab',
    availabilityPredicate: supportsOpenInNewTab,
    onAction: (context) => context.actions.openInNewTab(),
};
const paste: ElementMenuItem<'paste'> = {
    type: 'action',
    id: 'paste',
    localeKey: 'element.paste',
    shortcut: ['command', 'v'],
    availabilityPredicate: canPaste,
    onAction: (context) => context.actions.paste(),
};
const cut: ElementMenuItem<'cut'> = {
    type: 'action',
    id: 'cut',
    icon: Scissors01Icon,
    localeKey: 'element.cut',
    shortcut: ['command', 'x'],
    onAction: (context) => context.actions.cut(),
};
const copy: ElementMenuItem<'copy'> = {
    type: 'action',
    id: 'copy',
    icon: ClipboardCheckIcon,
    localeKey: 'element.copy',
    shortcut: ['command', 'c'],
    onAction: (context) => context.actions.copy(),
};
const pasteStyles: ElementMenuItem<'pasteStyles'> = {
    type: 'action',
    id: 'pasteStyles',
    localeKey: 'element.pasteStyles',
    shortcut: ['command', 'alt', 'v'],
    availabilityPredicate: canPasteStyles,
    onAction: (context) => context.actions.pasteStyles(),
};
const duplicateAlias: ElementMenuItem<'duplicate'> = {
    type: 'action',
    id: 'duplicate',
    localeKey: 'element.duplicateThisShortcut',
    shortcut: ['command', 'd'],
    availabilityPredicate: canDuplicateAliasToBoard,
    onAction: (context) => context.actions.duplicate(),
};
const duplicate: ElementMenuItem<'duplicate'> = {
    type: 'action',
    id: 'duplicate',
    icon: Copy01Icon,
    localeKey: 'element.duplicate',
    shortcut: ['command', 'd'],
    availabilityPredicate: (context) => !canDuplicateAliasToBoard(context),
    onAction: (context) => context.actions.duplicate(),
};
const duplicateLinkedBoard: ElementMenuItem<'duplicateAliasToBoard'> = {
    type: 'action',
    id: 'duplicateLinkedBoard',
    localeKey: 'element.duplicateLinkedBoard',
    availabilityPredicate: canDuplicateAliasToBoard,
    onAction: (context) => context.actions.duplicateAliasToBoard(),
};
const clone: ElementMenuItem<'clone'> = {
    type: 'action',
    id: 'clone',
    localeKey: 'element.createSyncedCopy',
    availabilityPredicate: canClone,
    onAction: (context) => context.actions.clone(),
};
const moveToTrash: ElementMenuItem<'moveToTrash'> = {
    type: 'action',
    id: 'delete',
    icon: Trash02Icon,
    localeKey: 'element.delete',
    shortcut: (context) => (isApplePlatform(context) ? ['delete'] : ['backspace']),
    onAction: (context) => context.actions.moveToTrash(),
};
const rename: ElementMenuItem<'rename'> = {
    type: 'action',
    id: 'rename',
    localeKey: 'element.rename',
    shortcut: ['return'],
    availabilityPredicate: canRename,
    onAction: (context) => context.actions.rename(),
};
const createShortcut: ElementMenuItem<'createShortcut'> = {
    type: 'action',
    id: 'createShortcut',
    localeKey: 'element.createShortcut',
    availabilityPredicate: (context) => isSingleBoard(context) && !canDuplicateAliasToBoard(context),
    onAction: (context) => context.actions.createShortcut(),
};

export const ELEMENT_CORE_CONTEXT_MENU_ITEMS = {
    openInNewTab,
    paste,
    cut,
    copy,
    pasteStyles,
    duplicateAlias,
    duplicate,
    duplicateLinkedBoard,
    clone,
    moveToTrash,
    rename,
    createShortcut,
};
